import { useEffect } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import ModalBasicLayout from "./ModalBasicLayout";
import TextField from "../Form/TextField";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import {
  isDuplicateFolderName,
  normalizeFolderName,
} from "../../utils/contentFolderUtils";
import {
  BUTTON_CANCEL,
  ERROR,
  FIELD_REQUIRED,
  GALLERY_FOLDERS_DUPLICATE,
  GALLERY_FOLDERS_NAME,
} from "../../i18n/keysTranslations";

const ModalFolderNameForm = ({
  modalOpen = false,
  folders = [],
  parentFolderID = null,
  excludeFolderID = null,
  parentFolderLabel = null,
  parentFolderLabelPrefix = null,
  initialName = "",
  title = "",
  ariaLabel = "",
  primaryButtonText = "",
  errorText = "",
  successText = "",
  isLoading = false,
  onCloseModal = () => {},
  onSubmitName = async () => ({ ok: false }),
}) => {
  const { t } = useTranslationApp();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (modalOpen) {
      reset({ name: initialName });
    }
  }, [initialName, modalOpen, reset]);

  const onSubmit = async ({ name }) => {
    const normalizedName = normalizeFolderName(name);

    if (
      isDuplicateFolderName({
        folders,
        parentFolderID,
        name: normalizedName,
        excludeFolderID,
      })
    ) {
      setError("name", {
        type: "validate",
        message: t(GALLERY_FOLDERS_DUPLICATE),
      });
      return;
    }

    const response = await onSubmitName({ name: normalizedName });

    if (response?.reason === "duplicateName") {
      setError("name", {
        type: "validate",
        message: t(GALLERY_FOLDERS_DUPLICATE),
      });
      return;
    }

    if (!response?.ok) {
      await SimpleAlert({
        icon: "error",
        title: t(ERROR),
        text: t(errorText),
      });
      return;
    }

    await SimpleAlert({
      icon: "success",
      title: t(successText),
    });
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(title)}
      ariaLabel={t(ariaLabel)}
      maxWidth="sm"
      fullWidth
    >
      {parentFolderLabel && parentFolderLabelPrefix ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(parentFolderLabelPrefix)}: {parentFolderLabel}
        </Typography>
      ) : null}
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextField
          name="name"
          control={control}
          errors={errors}
          label={t(GALLERY_FOLDERS_NAME)}
          variant="filled"
          fullWidth
          autoFocus
          disabled={isLoading}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) =>
              normalizeFolderName(value).length > 0 || t(FIELD_REQUIRED),
          }}
        />
      </Box>
      <DialogActions
        sx={{
          px: {
            xs: 3,
            sm: 5,
          },
          pb: 3,
          mt: 0.5,
        }}
      >
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
            disabled: isLoading,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: primaryButtonText,
            onClick: handleSubmit(onSubmit),
            loading: isLoading,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalFolderNameForm;
